import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Grid,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import roleAdminService from '../../../services/roleAdminService';
import { useSnackbar } from 'notistack';
import { ChromePicker } from 'react-color';

// Liste des icônes disponibles
const availableIcons = [
    { value: 'AdminPanelSettings', label: 'Admin' },
    { value: 'Security', label: 'Sécurité' },
    { value: 'People', label: 'Utilisateurs' },
    { value: 'Category', label: 'Catégories' },
    { value: 'Edit', label: 'Édition' },
    { value: 'Flag', label: 'Modération' },
    { value: 'Home', label: 'Propriétaire' },
    { value: 'Apartment', label: 'Locataire' },
    { value: 'Star', label: 'Par défaut' }
];

const RoleForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isSystem: false,
        isActive: true,
        displayOrder: 100,
        icon: 'Star',
        color: '#607D8B'
    });

    const [loading, setLoading] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchRole();
        }
    }, [id]);

    const fetchRole = async () => {
        setLoading(true);
        try {
            const role = await roleAdminService.getRole(id);
            if (role) {
                setFormData({
                    name: role.name || '',
                    description: role.description || '',
                    isSystem: role.isSystem || false,
                    isActive: role.isActive !== undefined ? role.isActive : true,
                    displayOrder: role.displayOrder || 100,
                    icon: role.icon || 'Star',
                    color: role.color || '#607D8B'
                });
            } else {
                enqueueSnackbar('Rôle non trouvé', { variant: 'error' });
                navigate('/admin/roles');
            }
        } catch (error) {
            enqueueSnackbar('Erreur lors du chargement du rôle', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleColorChange = (color) => {
        setFormData(prev => ({
            ...prev,
            color: color.hex
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isEditMode) {
                await roleAdminService.updateRole(id, formData);
                enqueueSnackbar('Rôle mis à jour avec succès', { variant: 'success' });
            } else {
                await roleAdminService.createRole(formData);
                enqueueSnackbar('Rôle créé avec succès', { variant: 'success' });
            }
            navigate('/admin/roles');
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                enqueueSnackbar(error.response.data.message, { variant: 'error' });
            } else {
                enqueueSnackbar(`Erreur lors de ${isEditMode ? 'la mise à jour' : 'la création'} du rôle`, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/roles');
    };

    if (loading && isEditMode) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" component="h1" mb={3}>
                {isEditMode ? 'Modifier le rôle' : 'Créer un nouveau rôle'}
            </Typography>

            <Paper elevation={2} sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nom du rôle"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={formData.isSystem}
                                helperText={formData.isSystem ? "Le nom d'un rôle système ne peut pas être modifié" : ""}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Ordre d'affichage"
                                name="displayOrder"
                                type="number"
                                value={formData.displayOrder}
                                onChange={handleChange}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="icon-label">Icône</InputLabel>
                                <Select
                                    labelId="icon-label"
                                    name="icon"
                                    value={formData.icon}
                                    onChange={handleChange}
                                    label="Icône"
                                >
                                    {availableIcons.map(icon => (
                                        <MenuItem key={icon.value} value={icon.value}>
                                            {icon.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Couleur"
                                    name="color"
                                    value={formData.color}
                                    onClick={() => setShowColorPicker(true)}
                                    InputProps={{
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 1,
                                                    backgroundColor: formData.color,
                                                    marginRight: 1
                                                }}
                                            />
                                        ),
                                        readOnly: true
                                    }}
                                />
                                {showColorPicker && (
                                    <Box position="relative" mt={1}>
                                        <Box position="absolute" zIndex={1}>
                                            <Box position="fixed" top={0} right={0} bottom={0} left={0} onClick={() => setShowColorPicker(false)} />
                                            <ChromePicker color={formData.color} onChange={handleColorChange} />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        name="isActive"
                                        color="primary"
                                    />
                                }
                                label="Actif"
                            />
                        </Grid>

                        {isEditMode && (
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isSystem}
                                            onChange={handleChange}
                                            name="isSystem"
                                            color="primary"
                                            disabled={true}
                                        />
                                    }
                                    label="Rôle système"
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : isEditMode ? 'Mettre à jour' : 'Créer'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default RoleForm;